import { MDText } from 'i18n-react';

import { PaperIcon, TransferIcon } from '@motorway/mw-highway-code';
import { InfoBox, InfoBoxProps } from '@motorway/mw-highway-code/alpha';
import { INFOBOX_VARIANT } from '@motorway/mw-highway-code/enums';

import { formatPriceTransactional } from 'Utilities/formatPrice';

import { CollectionDetailsList } from '../../../../vehicleCollection/CollectionDetails/CollectionDetails';
import { usePaymentDetailsSub } from '../../../../vehicleCollection/hooks/usePaymentDetailsSub';
import { useSaleDetailsSub } from '../../../../vehicleCollection/hooks/useSaleDetailsSub';
import { useVehicleCollectionSub } from '../../../../vehicleCollection/hooks/useVehicleCollectionSub';
import { validateHasTransactionalValue } from '../../../../vehicleCollection/utils/validator';

import LocalTexts from './PaymentInformationCard.texts.json';

import styles from './PaymentInformationCard.module.scss';

const LocalT = new MDText(LocalTexts);

export const OverpaymentInfoBox = ({ size = 'regular' }: Pick<InfoBoxProps, 'size'>) => {
  const { financeStatusData, isSaleComplete } = useVehicleCollectionSub();

  const [isOverpayment, overpaymentValue] = financeStatusData?.isNegativeEquity ? [
    financeStatusData.financeStatus.seller.status === 'overpaid',
    Math.abs(financeStatusData.financeStatus.seller.amountRemaining),
  ] : [false, 0];

  if (!isOverpayment) {
    return null;
  }

  return (
    <InfoBox
      content={LocalT.translate(`overpaidInfoBox.${isSaleComplete ? 'postPaymentContent' : 'prePaymentContent'}`)}
      heading={LocalT.translate('overpaidInfoBox.heading', { amount: formatPriceTransactional(overpaymentValue) })}
      size={size}
      variant={INFOBOX_VARIANT.INFO}
    />
  );
};

export const PaymentDetails = () => {
  const { financingAmount } = useSaleDetailsSub();
  const { reference } = usePaymentDetailsSub();

  const detailItems = [
    {
      content: <span className={styles.figure}>{reference}</span>,
      icon: <PaperIcon filled={false} />,
      id: 'paymentReference',
      shouldDisplay: Boolean(reference),
      title: LocalT.translate('paymentDetails.reference.title'),
    },
    {

      content: <span className={styles.figure}>{formatPriceTransactional(Math.abs(financingAmount!))}</span>,
      icon: <TransferIcon filled={false} />,
      id: 'financeFigure',
      shouldDisplay: validateHasTransactionalValue(financingAmount),
      title: LocalT.translate('paymentDetails.finance.title'),
    },
  ];

  return <CollectionDetailsList items={detailItems} title={null}/>;
};

export const PaymentInformation = () => {
  const { financingAmount } = useSaleDetailsSub();

  const hasFinance = validateHasTransactionalValue(financingAmount);

  return (
    <div className={styles.paymentInformationCard}>
      <h2 className={styles.title}>{LocalT.translate('heading')}</h2>
      {hasFinance ? <p data-thc-style-override className={styles.description}>{LocalT.translate('description')}</p> : null}
      <OverpaymentInfoBox />
      <PaymentDetails />
    </div>
  );
};

export const PaymentInformationCard = () => {
  const { isSaleComplete } = useVehicleCollectionSub();

  if (!isSaleComplete) {
    return null;
  }

  return <PaymentInformation />;
};
